import { useEffect, useRef, useState } from 'react'
import { Search, ChevronsUpDown, Check, Plus, Sun, Moon, LogOut, Building2 } from 'lucide-react'
import type { Clinica } from '../lib/sessao'
import './cabecalho.css'

/**
 * A faixa do topo.
 *
 * ─── POR QUE ELA PASSOU A EXISTIR ─────────────────────────────────
 *
 * `docs/design-composicao.md` §1: "duas camadas de cabeçalho, não uma".
 * As referências separam uma faixa no topo — busca global e identidade
 * — do cabeçalho da tela, que fica já dentro do conteúdo. Nós
 * tínhamos as duas coisas na barra lateral, e o diagnóstico escrito lá
 * é literal: "por isso o nome da clínica brigou com o menu".
 *
 * O rail volta a ser só navegação. Aqui ficam as três coisas que
 * pertencem à SESSÃO, não à tela: em qual unidade você está, a busca,
 * e quem você é.
 *
 * ─── O SELETOR DE UNIDADE ─────────────────────────────────────────
 *
 * `app.criar_unidade` existe no banco desde a 0032 e `POST
 * /v1/conta/unidade` desde então na API. Não havia tela: quem tinha
 * duas clínicas escolhia uma no login e, para ir à outra, saía e
 * entrava de novo.
 *
 * O seletor só aparece com MAIS DE UMA unidade. Com uma, ele viraria
 * um menu de um item só — ruído que ensina a pessoa a ignorar o
 * lugar onde a informação mais importante da tela mora. Com uma
 * unidade o nome fica lá, como texto.
 */
export function Cabecalho({
  clinicas, tenantId, papel, temaEscuro,
  aoTrocarClinica, aoNovaUnidade, aoBuscar, aoTrocarTema, aoSair, usuario,
}: {
  clinicas: Clinica[]
  tenantId: string | null
  papel: string | null
  usuario: string | null
  temaEscuro: boolean
  aoTrocarClinica: (tenantId: string) => void
  aoNovaUnidade: () => void
  aoBuscar: () => void
  aoTrocarTema: () => void
  aoSair: () => void
}) {
  const atual = clinicas.find((c) => c.tenantId === tenantId) ?? null
  const varias = clinicas.length > 1

  return (
    <header className="topo">
      <div className="topo__esq">
        {varias
          ? <SeletorUnidade clinicas={clinicas} atual={atual}
                            aoTrocar={aoTrocarClinica} aoNova={aoNovaUnidade} />
          : (
            <div className="topo__unica">
              <span className="topo__glifo" aria-hidden="true">
                <Building2 size={16} strokeWidth={1.9} />
              </span>
              <span className="topo__nome">{atual?.nome ?? '—'}</span>
            </div>
          )}
      </div>

      {/* A busca no MEIO e larga, como nas referências: ela é da sessão
          inteira, não de uma tela. O ⌘K continua funcionando de
          qualquer lugar; isto é a porta visível dele. */}
      <button className="topo__buscar" onClick={aoBuscar}>
        <Search size={15} strokeWidth={1.9} aria-hidden="true" />
        <span>Buscar paciente, profissional, procedimento…</span>
        <kbd>⌘K</kbd>
      </button>

      <div className="topo__dir">
        <button className="topo__acao" onClick={aoTrocarTema}
                aria-label={temaEscuro ? 'Usar tema claro' : 'Usar tema escuro'}
                title={temaEscuro ? 'Tema claro' : 'Tema escuro'}>
          {temaEscuro ? <Sun size={16} strokeWidth={1.9} /> : <Moon size={16} strokeWidth={1.9} />}
        </button>
        <div className="topo__eu">
          <span className="topo__eu-nome">{usuario}</span>
          {papel && <span className="topo__eu-papel">{rotuloPapel(papel)}</span>}
        </div>
        <button className="topo__acao" onClick={aoSair}
                aria-label="Sair da conta" title="Sair">
          <LogOut size={16} strokeWidth={1.9} />
        </button>
      </div>
    </header>
  )
}

/** DONO vira "Dona da clínica" ao lado de um nome; o papel cru é do banco. */
const PAPEIS: Record<string, string> = {
  DONO: 'Dono', GESTOR: 'Gestão', PROFISSIONAL: 'Atende',
  RECEPCAO: 'Recepção', FINANCEIRO: 'Financeiro', LEITURA: 'Só leitura',
}
const rotuloPapel = (p: string) => PAPEIS[p] ?? p

function SeletorUnidade({ clinicas, atual, aoTrocar, aoNova }: {
  clinicas: Clinica[]
  atual: Clinica | null
  aoTrocar: (t: string) => void
  aoNova: () => void
}) {
  const [aberto, setAberto] = useState(false)
  const caixa = useRef<HTMLDivElement>(null)

  /* Fecha ao clicar fora e no Escape. Sem isto o menu fica pendurado
     sobre a tela seguinte quando a pessoa navega por teclado. */
  useEffect(() => {
    if (!aberto) return
    const foraDaCaixa = (e: MouseEvent) => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) setAberto(false)
    }
    const noEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') setAberto(false) }
    document.addEventListener('mousedown', foraDaCaixa)
    document.addEventListener('keydown', noEscape)
    return () => {
      document.removeEventListener('mousedown', foraDaCaixa)
      document.removeEventListener('keydown', noEscape)
    }
  }, [aberto])

  return (
    <div className="unid" ref={caixa}>
      <button className="unid__botao" onClick={() => setAberto(!aberto)}
              aria-expanded={aberto} aria-haspopup="listbox">
        <span className="topo__glifo" aria-hidden="true">
          <Building2 size={16} strokeWidth={1.9} />
        </span>
        <span className="unid__nome">{atual?.nome ?? 'Escolher unidade'}</span>
        <ChevronsUpDown size={14} strokeWidth={2} aria-hidden="true" />
      </button>

      {aberto && (
        <div className="unid__menu" role="listbox"
             aria-label="Unidades em que você trabalha">
          <p className="unid__titulo">Suas unidades</p>
          {clinicas.map((c) => {
            const eEsta = c.tenantId === atual?.tenantId
            return (
              <button key={c.tenantId} role="option" aria-selected={eEsta}
                      className={`unid__item${eEsta ? ' is-atual' : ''}`}
                      onClick={() => {
                        setAberto(false)
                        /* Trocar para a MESMA unidade recarregaria a
                           sessão à toa e piscaria a tela inteira. */
                        if (!eEsta) aoTrocar(c.tenantId)
                      }}>
                <span className="unid__item-nome">{c.nome}</span>
                <span className="unid__item-papel">{rotuloPapel(c.papel)}</span>
                {eEsta && <Check size={15} strokeWidth={2.2} aria-hidden="true" />}
              </button>
            )
          })}
          <hr className="unid__sep" />
          <button className="unid__nova" onClick={() => { setAberto(false); aoNova() }}>
            <Plus size={15} strokeWidth={2} aria-hidden="true" />
            Abrir outra unidade
          </button>
        </div>
      )}
    </div>
  )
}
